import React from 'react';
import { SmartphoneIcon, TabletIcon, MonitorIcon, HomeIcon, LockIcon, ShieldIcon, ExternalLinkIcon } from 'lucide-react';
export const LockScreen = () => {
  return <div className="flex-1 flex flex-col bg-[#FFFAE4] overflow-hidden" data-id="element-1304">
      {/* Header */}
      <div className="bg-[#FFFAE4] shadow-sm border-b border-[#E5C1A9] p-4" data-id="element-1305">
        <div className="max-w-7xl mx-auto" data-id="element-1306">
          <h1 className="text-2xl font-bold text-[#6B4B3E]" data-id="element-1307">
            Parental Controls
          </h1>
          <p className="text-sm text-[#6B4B3E]/75" data-id="element-1308">
            Simple steps to lock your screen
          </p>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-auto" data-id="element-1309">
        <div className="max-w-3xl mx-auto px-4 py-8" data-id="element-1310">
          {/* Introduction */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1311">
            <p className="text-[#6B4B3E]/80 text-lg leading-relaxed" data-id="element-1312">
              Story Bunny runs in your browser — not as an app — which means it
              works on any device without needing to download anything. Here are
              quick ways to keep www.storybunny.ai locked on your device.
            </p>
          </div>
          {/* Safety Note */}
          <div className="bg-[#FFF0E6] rounded-xl p-6 mb-8 flex items-start space-x-4" data-id="element-1313">
            <div className="text-2xl flex-shrink-0" data-id="element-1314">✨</div>
            <p className="text-[#6B4B3E]/80" data-id="element-1315">
              Story Bunny doesn't use pop-up ads or external links — everything
              stays within the story environment.
            </p>
          </div>
          {/* Shortcut Tip */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1316">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4 flex items-center" data-id="element-1317">
              <HomeIcon className="mr-2" data-id="element-1318" />
              Create a Shortcut
            </h2>
            <p className="text-[#6B4B3E]/80 mb-4" data-id="element-1319">
              Start Story Bunny straight from your Home Screen:
            </p>
            <ul className="space-y-2 text-[#6B4B3E]/80" data-id="element-1320">
              <li data-id="element-1321">
                On Safari (iOS): Tap{' '}
                <span className="font-medium" data-id="element-1322">Share icon</span> →{' '}
                <span className="font-medium" data-id="element-1323">Add to Home Screen</span>
              </li>
              <li data-id="element-1324">
                On Chrome (Android): Tap{' '}
                <span className="font-medium" data-id="element-1325">More</span> →{' '}
                <span className="font-medium" data-id="element-1326">Add to Home Screen</span>
              </li>
            </ul>
          </div>
          {/* iOS Instructions */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1327">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4 flex items-center" data-id="element-1328">
              <TabletIcon className="mr-2" data-id="element-1329" />
              iPad or iPhone Instructions
            </h2>
            <div className="mb-6" data-id="element-1330">
              <h3 className="font-bold text-[#6B4B3E] mb-3" data-id="element-1331">
                Step 1: Restrict Web Access to Only Story Bunny
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-[#6B4B3E]/80" data-id="element-1332">
                <li data-id="element-1333">
                  Go to <span className="font-medium" data-id="element-1334">Settings</span> →{' '}
                  <span className="font-medium" data-id="element-1335">Screen Time</span> →{' '}
                  <span className="font-medium" data-id="element-1336">
                    Content & Privacy Restrictions
                  </span>
                </li>
                <li data-id="element-1337">
                  Tap <span className="font-medium" data-id="element-1338">Content Restrictions</span>
                </li>
                <li data-id="element-1339">
                  Tap <span className="font-medium" data-id="element-1340">Web Content</span> → Select{' '}
                  <span className="font-medium" data-id="element-1341">Allowed Websites Only</span>
                </li>
                <li data-id="element-1342">Remove everything except www.storybunny.ai</li>
                <li data-id="element-1343">
                  Tap <span className="font-medium" data-id="element-1344">Add Website</span> → Type:
                  www.storybunny.ai
                </li>
              </ol>
              <p className="mt-2 text-[#6B4B3E]/80 italic" data-id="element-1345">
                This ensures your child can only access Story Bunny in Safari.
              </p>
            </div>
            <div data-id="element-1346">
              <h3 className="font-bold text-[#6B4B3E] mb-3" data-id="element-1347">
                Step 2: Turn on Guided Access
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-[#6B4B3E]/80" data-id="element-1348">
                <li data-id="element-1349">
                  Go to <span className="font-medium" data-id="element-1350">Settings</span> →{' '}
                  <span className="font-medium" data-id="element-1351">Accessibility</span> →{' '}
                  <span className="font-medium" data-id="element-1352">Guided Access</span>
                </li>
                <li data-id="element-1353">
                  Toggle <span className="font-medium" data-id="element-1354">Guided Access</span> ON
                </li>
                <li data-id="element-1355">
                  Tap <span className="font-medium" data-id="element-1356">Passcode Settings</span> →
                  Set a 4-digit code
                </li>
                <li data-id="element-1357">Open Safari and go to www.storybunny.ai</li>
                <li data-id="element-1358">
                  Triple-click the <span className="font-medium" data-id="element-1359">side</span> or{' '}
                  <span className="font-medium" data-id="element-1360">home button</span>
                </li>
                <li data-id="element-1361">
                  Tap <span className="font-medium" data-id="element-1362">Start</span> to begin
                  session
                </li>
              </ol>
              <p className="mt-2 text-[#6B4B3E]/80" data-id="element-1363">
                To exit: Triple-click again and enter your passcode
              </p>
            </div>
          </div>
          {/* Android Instructions */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1364">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4 flex items-center" data-id="element-1365">
              <SmartphoneIcon className="mr-2" data-id="element-1366" />
              Android Tablets or Phones
            </h2>
            <p className="text-[#6B4B3E]/80 mb-4" data-id="element-1367">
              Most Android devices have{' '}
              <span className="font-medium" data-id="element-1368">Screen Pinning</span>:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-[#6B4B3E]/80" data-id="element-1369">
              <li data-id="element-1370">
                Open <span className="font-medium" data-id="element-1371">Settings</span> →{' '}
                <span className="font-medium" data-id="element-1372">Security</span> →{' '}
                <span className="font-medium" data-id="element-1373">Screen Pinning</span> (or Search
                "Pin app")
              </li>
              <li data-id="element-1374">Turn it ON and enable a passcode</li>
              <li data-id="element-1375">Open Story Bunny in Chrome</li>
              <li data-id="element-1376">
                Tap <span className="font-medium" data-id="element-1377">Overview</span> button (square
                icon)
              </li>
              <li data-id="element-1378">Tap the pin icon on the app window</li>
            </ol>
            <p className="mt-2 text-[#6B4B3E]/80" data-id="element-1379">
              To unpin: Hold down{' '}
              <span className="font-medium" data-id="element-1380">Back + Overview</span> buttons
              together
            </p>
          </div>
          {/* Desktop Instructions */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1381">
            <h2 className="text-xl font-bold text-[#6B4B3E] mb-4 flex items-center" data-id="element-1382">
              <MonitorIcon className="mr-2" data-id="element-1383" />
              Mac or PC
            </h2>
            <p className="text-[#6B4B3E]/80 mb-4" data-id="element-1384">
              While there's no full lock feature for browsers, you can:
            </p>
            <ul className="space-y-2 text-[#6B4B3E]/80" data-id="element-1385">
              <li data-id="element-1386">
                • Switch to{' '}
                <span className="font-medium" data-id="element-1387">Full Screen Mode</span> (⌘ +
                Control + F on Mac, or F11 on Windows)
              </li>
              <li data-id="element-1388">
                • Temporarily{' '}
                <span className="font-medium" data-id="element-1389">disconnect keyboard/mouse</span>{' '}
                for toddlers
              </li>
              <li data-id="element-1390">
                • Use <span className="font-medium" data-id="element-1391">parental controls</span> to
                restrict access to other tabs or apps
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>;
};